import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';

import { ConfirmDialogService, ToastService, useDataTable, useDialogCrup } from '@iamsoftware/react-hooks';
import { AgreementTemplateService as Service } from './AgreementTemplateService';
import { FormFields } from './FormFields';
import { TemplateDetail } from './TemplateDetail';
import { CommonService } from '../../../service/CommonService';
import { AgreementConfigService } from '../AgreementConfigService';
import { Oauth2GoogleService } from '../../hop-dong/mau-hop-dong/Oauth2GoogleService';
import { UploadExcel } from '../../quan-ly-tai-lieu/UploadExcel';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
const statuses = [
  { value: 'N', label: 'Đang hoạt động' },
  { value: 'Y', label: 'Ngừng hoạt động' }
];

export const TemplateDocumentConfig: React.FC = () => {
  const dataKey = 'agreementId';
  const [statusFilter, setStatusFilter] = useState(statuses[0].value);
  const [keyword, setKeyword] = useState('');
  const [documentTypeOptions, setDocumentTypeOptions] = useState<any[]>([]);
  const [documentTypeMap, setDocumentTypeMap] = useState<Record<string, string>>({});
  const [displayUpload, setDisplayUpload] = useState(false);
  const [displayFileDetail, setDisplayFileDetail] = useState(false);
  const [formFieldsDisplay, setFormFieldsDisplay] = useState<any>(null);
  const [detailDialog, setDetailDialog] = useState<any>(null);
  const [templateData, setTemplateData] = useState<any>(null);
  const [fileName, setFileName] = useState('');
  const [filePreview, setFilePreview] = useState<boolean | number>(false);
  const [fileId, setFileId] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [filePreviewReady, setFilePreviewReady] = useState(false);
  const openedPreviewFileIdRef = useRef<string | null>(null);
  const [displayUploadExcel, setDisplayUploadExcel] = useState(false);
  const [selectedTemplateForBulk, setSelectedTemplateForBulk] = useState<any>(null);
  const header = 'Tài liệu mẫu';
  const toast = useRef<Toast>(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  useEffect(() => {
    CommonService.getAgreementType().then(data => {
      if (data?.listData) {
        const options = data.listData.map((item: any) => ({
          value: item.agreementPartyTypeConfId,
          label: item.agreementConfigureName
        }));
        setDocumentTypeOptions(options);
        const map: Record<string, string> = {};
        data.listData.forEach((item: any) => {
          if (item.agreementConfigureEnumId) {
            map[item.agreementConfigureEnumId] = item.agreementConfigureName;
          }
        });
        setDocumentTypeMap(map);
      }
    }).catch(() => { });
  }, []);

  useEffect(() => {
    reloadLazyData();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mở permission cho Google Drive file khi mở preview
  useEffect(() => {
    const currentFileId = fileId || templateData?.fileId || formFieldsDisplay?.fileId;
    if (filePreview && currentFileId && !openedPreviewFileIdRef.current) {
      Oauth2GoogleService.openAnyoneView(currentFileId)
        .then(() => {
          openedPreviewFileIdRef.current = currentFileId;
          setFilePreviewReady(true);
          // Đóng permission ngay sau khi mở vì preview không cần giữ permission mở
          setTimeout(() => {
            Oauth2GoogleService.closeEditor(currentFileId)
              .then(() => {
                openedPreviewFileIdRef.current = null;
              })
              .catch((err) => {
                console.error('Error closing file permission after preview:', err);
              });
          }, 1000); // Đợi 1 giây để đảm bảo file đã load
        })
        .catch((err) => {
          console.error('Error opening file permission:', err);
          ToastService.error('Không thể mở quyền truy cập file');
        });
    } else if (!filePreview) {
      // Reset state khi đóng dialog
      if (openedPreviewFileIdRef.current) {
        openedPreviewFileIdRef.current = null;
        setFilePreviewReady(false);
      }
    }
  }, [filePreview, fileId, templateData?.fileId, formFieldsDisplay?.fileId])

  // Cleanup khi component unmount
  useEffect(() => {
    return () => {
      if (openedPreviewFileIdRef.current) {
        Oauth2GoogleService.closeEditor(openedPreviewFileIdRef.current).catch(err => {
          console.error('Error closing file permission on unmount:', err);
        });
        openedPreviewFileIdRef.current = null;
      }
    };
  }, [])
  const handleTabChange = (e: { index: number }) => {
    setActiveTabIndex(e.index);
  };
  const { render: renderDataTable, selectedItems, reloadLazyData } = useDataTable({
    tableHeader: (
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">

        <Button
          className="ml-auto"
          label="Tạo tài liệu mẫu"
          icon="pi pi-plus"
          size="small"
          severity="success"
          onClick={handleCreate}
        />
      </div>
    ),
    dataKey,
    columns: [
      {
        field: 'agreementPseudoId',
        header: 'Mã tài liệu mẫu',
        width: 130,
        matchMode: 'contains',
        dataType: 'custom',
        customCell(rowData: any) {
          return (
            <span className="underline text-primary cursor-pointer"
              onDoubleClick={() => handleEdit(rowData, true)}
              title="Double click để xem chi tiết">
              {rowData.agreementPseudoId}
            </span>
          );
        }
      },
      { field: 'agreementName', header: 'Tên tài liệu mẫu', width: 220, matchMode: 'contains' },
      { field: 'fileName', header: 'Tên file mẫu', width: 200, matchMode: 'contains' },
      {
        field: 'agreementConfigureEnumId',
        header: 'Loại tài liệu',
        width: 220,
        dataType: 'custom',
        customCell(rowData: any) {
          const code = rowData.agreementConfigureEnumId;
          return documentTypeMap[code] || code || '';
        }
      },
      { field: 'agreementDate', header: 'Ngày tạo', width: 130, dataType: 'date' },
      { field: 'description', header: 'Mô tả', width: 220, matchMode: 'contains' },
      { field: 'status', header: 'Trạng thái', width: 140 }
    ],
    indexColumnWidth: 45,
    actionColumnWidth: 120,
    getList: lazyLoadEvent => {
      const params = keyword ? { textSearch: keyword.trim() } : undefined;
      return Service.getAgreementList(statusFilter, lazyLoadEvent, params);
    },
    initActions(items: any[]) {
      items?.forEach(item => {
        const actions: any[] = [
          { icon: 'pi pi-pencil', title: 'Chỉnh sửa', className: 'p-button-warning', command: () => handleEdit(item) },
          { icon: 'pi pi-eye', title: 'Xem chi tiết', className: 'p-button-info', command: () => handleEdit(item, true) }
        ];
        if (item.statusId === 'AGT_ACTIVE') {
          actions.push({
            icon: 'pi pi-lock',
            title: 'Khóa',
            className: 'p-button-danger',
            command: () => handleLock(item)
          });
          actions.push({
            icon: 'pi pi-file-excel',
            title: 'Tạo tài liệu hàng loạt',
            className: 'p-button-info',
            command: () => handleCreateBulk(item)
          });
        } else if (item.statusId === 'AGT_INACTIVE') {
          actions.push({
            icon: 'pi pi-unlock',
            title: 'Mở khóa',
            className: 'p-button-success',
            command: () => handleUnlock(item)
          });
        }
        item.actions = actions;
      });
    }
  });

  const selectedRow = selectedItems?.[dataKey] ? selectedItems : null;

  const { render: renderDialogCrup, create, update } = useDialogCrup({
    header: 'Tài liệu mẫu',
    dataKey,
    width: '40rem',
    fields: [
      { field: 'agreementPseudoId', header: 'Mã tài liệu mẫu', required: true, className: 'md:col-12' },
      { field: 'agreementName', header: 'Tên tài liệu mẫu', required: true, className: 'md:col-12' },
      {
        field: 'agreementPartyTypeConfId',
        header: 'Loại tài liệu',
        required: true,
        className: 'md:col-12',
        type: 'Dropdown',
        DropdownProps: {
          options: documentTypeOptions,
          optionLabel: 'label',
          optionValue: 'value',
          placeholder: 'Chọn loại tài liệu'
        }
      },
      {
        field: 'contentFile',
        header: 'File mẫu (.docx)',
        required: true,
        displayDependency: () => displayUpload,
        type: 'file',
        InputFileProps: { accept: '.docx' },
        className: 'md:col-12'
      },
      {
        field: 'fileName',
        header: 'File mẫu (.docx)',
        display: displayFileDetail,
        type: 'custom',
        className: 'md:col-12',
        body: (
          <Fragment>
            <label>File mẫu (.docx)</label>
            <div className="p-inputgroup flex-1">
              <InputText value={fileName} disabled />
              <span className="p-inputgroup-addon"
                style={{ background: '#22C55E', color: '#fff', cursor: 'pointer' }}
                onClick={() => {
                  const agreementId = templateData?.agreementId || formFieldsDisplay?.agreementId;
                  if (agreementId) {
                    const currentFileId = templateData?.fileId || formFieldsDisplay?.fileId;
                    const currentFileUrl = templateData?.fileUrl || formFieldsDisplay?.fileUrl;
                    if (currentFileId || currentFileUrl) {
                      setFileId(currentFileId || null);
                      setFileUrl(currentFileUrl || null);
                      setFilePreview(Date.now());
                    } else {
                      ToastService.error('Chưa có file để xem');
                    }
                  } else {
                    ToastService.error('Chưa có file để xem');
                  }
                }}>
                <i className="pi pi-eye" />
              </span>
              <span className="p-inputgroup-addon"
                style={{ background: '#F59E0B', color: '#fff', cursor: 'pointer' }}
                onClick={() => switchUploadMode()}>
                <i className="pi pi-pencil" />
              </span>
            </div>
          </Fragment>
        )
      },
      { field: 'description', header: 'Mô tả', type: 'InputTextarea', className: 'md:col-12' }
    ],
    createItem: async item => {
      const payload = await normalizePayload(item);
      return Service.create(payload).then(data => {
        if (displayUpload) {
          setFormFieldsDisplay(data);
          setFileId(data.fileId || null);
          setFileUrl(data.fileUrl || null);
        }
      });
    },
    updateItem: async (id, item) => {
      const payload = await normalizePayload(item);
      return Service.update(id, payload).then(data => {
        if (displayUpload) {
          setFormFieldsDisplay(data);
          setFileId(data.fileId || null);
          setFileUrl(data.fileUrl || null);
        }
      });
    },
    reloadLazyData,
    useSaveSplitButton: false
  });

  const normalizePayload = async (item: any) => {
    if (item.contentFile?.length) {
      item.contentFile = item.contentFile[0];
    }
    delete item.categories;
    return Service.toFormData(item);
  };

  function resolveDataKey(rowData?: any) {
    if (rowData && rowData[dataKey]) return rowData[dataKey];
    if (selectedRow && selectedRow[dataKey]) return selectedRow[dataKey];
    return null;
  }

  function handleCreate() {
    setDisplayUpload(true);
    setDisplayFileDetail(false);
    setTemplateData(null);
    setFileName('');
    setFileId(null);
    setFileUrl(null);
    create();
  }

  function handleEdit(rowData?: any, viewOnly?: boolean) {
    setDisplayUpload(false);
    setDisplayFileDetail(true);
    const id = resolveDataKey(rowData);
    if (!id) return;

    if (viewOnly) {
      setDetailDialog({ agreementId: id });
      return;
    }

    Service.get(id).then(data => {
      if (data.productCategoryId) {
        data.categories = { value: data.productCategoryId, label: `[${data.categoryPseudoId}] - ${data.categoryName}` };
      }
      setTemplateData(data);
      setFileName(data.fileName);
      setFileId(data.fileId || null);
      setFileUrl(data.fileUrl || null);
      update(data);
    });
  }

  function handleLock(item: any) {
    ConfirmDialogService.confirm(
      'Khóa tài liệu mẫu',
      'Bạn xác nhận khóa tài liệu mẫu này?',
      () => {
        Service.disable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }

  function handleCreateBulk(item: any) {
    setSelectedTemplateForBulk(item);
    setDisplayUploadExcel(true);
  }

  function handleUnlock(item: any) {
    ConfirmDialogService.confirm(
      'Mở khóa tài liệu mẫu',
      'Bạn xác nhận mở khóa tài liệu mẫu này?',
      () => {
        Service.enable(item.agreementId).then(() => {
          ToastService.success();
          reloadLazyData();
        });
      }
    );
  }

  function switchUploadMode() {
    setDisplayUpload(true);
    setDisplayFileDetail(false);
  }

  return (
    <div className="grid">
      <div className="col-12">
        <h3 className="m-0 mb-2">Quản lý tài liệu mẫu</h3>
      </div>
      <div className="col-12 pb-0" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
            <Toast ref={toast} />
            <TabView activeIndex={activeTabIndex} onTabChange={handleTabChange}>
              <TabPanel header="Đang hoạt động">
              </TabPanel>
              <TabPanel header="Ngừng hoạt động">
              </TabPanel>
            </TabView>
          </div>
      <div className="col-12 pb-0" style={{ maxHeight: 'calc(100vh - 12rem)' }}>
        {renderDataTable()}
      </div>

      {renderDialogCrup()}
      <FormFields display={formFieldsDisplay} setDisplay={setFormFieldsDisplay} />
      <TemplateDetail display={detailDialog} setDisplay={setDetailDialog} />
      <UploadExcel
        display={displayUploadExcel}
        setDisplay={(value) => {
          setDisplayUploadExcel(value);
          if (!value) {
            setSelectedTemplateForBulk(null);
          }
        }}
        reloadLazyData={reloadLazyData}
        agreementId={selectedTemplateForBulk?.agreementId}
      />
      <Dialog
        header="File tài liệu mẫu"
        visible={!!filePreview}
        position="top"
        draggable={false}
        resizable={false}
        modal
        style={{ width: '45vw' }}
        onHide={() => {
          setFilePreview(false);
        }}
        footer={
          <div>
            <Button label="Đóng" icon="pi pi-times" className="p-button-outlined p-button-secondary p-button-sm"
              type="button" onClick={() => setFilePreview(false)} />
          </div>
        }
      >
        {(() => {
          const currentFileId = fileId || templateData?.fileId || formFieldsDisplay?.fileId;
          const currentFileUrl = fileUrl || templateData?.fileUrl || formFieldsDisplay?.fileUrl;

          if (currentFileId && filePreviewReady) {
            // Hiển thị Google Drive file dưới dạng PDF preview
            const previewUrl = currentFileUrl
              ? currentFileUrl.replace('/edit', '/preview').replace('/view', '/preview')
              : `https://drive.google.com/file/d/${currentFileId}/preview`;

            return (
              <iframe
                className={'file-tab-panel'}
                src={previewUrl}
                title="File tài liệu mẫu"
                style={{ width: '100%', height: 'calc(100vh - 250px)', border: 'none' }}
              />
            );
          }

          if (currentFileId && !filePreviewReady) {
            return (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
              </div>
            );
          }

          if (!templateData?.agreementId && !formFieldsDisplay?.agreementId) {
            return <p className="text-sm text-600 m-0">Không có file để hiển thị.</p>;
          }

          return <p className="text-sm text-600 m-0">Đang tải file...</p>;
        })()}
      </Dialog>
    </div>
  );
};
